import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import {
  getPlatformTenant,
  getSelectedPlatformTenant,
  mapLaravelError,
} from '../../../services/platformTenantsApi';
import { fetchRoleOptions } from '../../../services/applicationRolesApi';
import {
  evaluateLicenseProvisioning,
  formatLicenseSummary,
  formatLicenseWindow,
  licenseStatusColor,
  licenseStatusLabel,
} from '../../../utils/tenantLicense';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  PersonRounded as IdentityIcon,
  WorkOutlineRounded as RoleIcon,
  DescriptionRounded as ContractIcon,
  BadgeRounded as DocsIcon,
  AutoAwesomeRounded as SparkleIcon,
  CheckRounded as CheckIcon,
  ArrowBackRounded as BackIcon,
  ArrowForwardRounded as ForwardIcon,
  SaveRounded as SaveIcon,
  PhotoCameraRounded as PhotoIcon,
} from '@mui/icons-material';

import { PageLayout } from '../../../components/ui';

const FALLBACK_ROLE_OPTIONS = [
  { value: 'employee', label: 'Employee' },
  { value: 'manager', label: 'Manager' },
  { value: 'hr_admin', label: 'HR Admin' },
  { value: 'finance', label: 'Finance' },
  { value: 'finance_coordinator', label: 'Finance Coordinator' },
  { value: 'ceo', label: 'CEO' },
];

const CONTRACT_TYPES = ['30 Days Yearly', '24 Days Yearly', '21 Days Yearly', '20 Days Yearly'];

const STEPS = [
  { id: 1, label: 'Identity', icon: <IdentityIcon fontSize="small" />, hint: "Let's start with who they are." },
  { id: 2, label: 'Role', icon: <RoleIcon fontSize="small" />, hint: 'Where do they sit in the org?' },
  { id: 3, label: 'Contract', icon: <ContractIcon fontSize="small" />, hint: 'Terms, dates and pay.' },
  { id: 4, label: 'Documents', icon: <DocsIcon fontSize="small" />, hint: 'IDs and photos — almost done.' },
];

function getInitials(name) {
  return (
    String(name || '')
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0])
      .join('')
      .toUpperCase() || '—'
  );
}

function roleTone(roleValue) {
  const v = String(roleValue || '').toLowerCase();
  if (v.includes('manager') || v.includes('hod') || v.includes('operation') || v.includes('head')) return 'success';
  if (v.includes('finance')) return 'warning';
  if (v === 'ceo' || v.includes('executive')) return 'secondary';
  if (v.includes('hr')) return 'primary';
  return 'info';
}

function CompletionRing({ pct }) {
  const theme = useTheme();
  const r = 28;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  return (
    <svg width={62} height={62} viewBox="0 0 72 72" role="img" aria-label={`${pct} percent complete`}>
      <circle cx="36" cy="36" r={r} fill="none" stroke={alpha(theme.palette.text.primary, 0.12)} strokeWidth="8" />
      <circle
        cx="36"
        cy="36"
        r={r}
        fill="none"
        stroke={theme.palette.success.main}
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${c}`}
        transform="rotate(-90 36 36)"
        style={{ transition: 'stroke-dasharray .4s ease' }}
      />
      <text x="36" y="40" textAnchor="middle" fontSize="15" fontWeight="500" fill={theme.palette.text.primary} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {pct}%
      </text>
    </svg>
  );
}

function IdCard({ form, tone, roleLabel }) {
  const theme = useTheme();
  const accent = theme.palette[tone]?.main || theme.palette.info.main;
  return (
    <Box sx={{ perspective: '900px' }}>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: '16px',
          overflow: 'hidden',
          transition: 'transform .3s ease',
          transformStyle: 'preserve-3d',
          '&:hover': { transform: 'rotateX(5deg) rotateY(-6deg) translateY(-3px)' },
        }}
      >
        <Box sx={{ height: 58, position: 'relative', background: `linear-gradient(125deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.main})` }}>
          <Box sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', width: 34, height: 7, borderRadius: 999, backgroundColor: alpha('#fff', 0.45) }} />
        </Box>
        <Box sx={{ textAlign: 'center', px: 2 }}>
          <Box
            sx={{
              width: 66,
              height: 66,
              borderRadius: '19px',
              mx: 'auto',
              mt: '-34px',
              display: 'grid',
              placeItems: 'center',
              fontSize: 23,
              fontWeight: 700,
              color: '#fff',
              backgroundColor: theme.palette.primary.main,
              border: `3px solid ${theme.palette.background.paper}`,
              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.18)}`,
            }}
          >
            {getInitials(form.name)}
          </Box>
          <Typography sx={{ fontWeight: 700, mt: 0.8 }}>{form.name || 'New employee'}</Typography>
          <Typography variant="caption" color="text.secondary">{form.position || 'Position'}</Typography>
          <Box sx={{ mt: 0.9 }}>
            <Chip size="small" label={roleLabel || form.role || 'Employee'} sx={{ borderRadius: 999, backgroundColor: alpha(accent, 0.16), color: theme.palette[tone]?.dark || accent, fontWeight: 600 }} />
          </Box>
        </Box>
        <Box sx={{ px: 2, pt: 1, pb: 1.1 }}>
          {[
            ['Code', form.employee_code],
            ['Department', form.department],
            ['Joining', form.joining_date],
          ].map(([k, v]) => (
            <Stack key={k} direction="row" justifyContent="space-between" sx={{ py: 0.6, fontSize: 12, borderTop: `0.5px solid ${theme.palette.divider}` }}>
              <Typography variant="caption" color="text.secondary">{k}</Typography>
              <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>{v || '—'}</Typography>
            </Stack>
          ))}
          <Box
            sx={{
              height: 22,
              mt: 1,
              opacity: 0.5,
              backgroundImage: `repeating-linear-gradient(90deg, ${theme.palette.text.primary} 0 1px, transparent 1px 3px, ${theme.palette.text.primary} 3px 4px, transparent 4px 7px)`,
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', letterSpacing: '0.18em', mt: 0.7, fontSize: 10 }}>
            EMPLOYEE ID
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

export default function AddUserPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const reduced = useReducedMotion();
  const isPlatformAdmin = localStorage.getItem('is_platform_admin') === '1';
  const selectedPlatformTenant = getSelectedPlatformTenant();
  const hasPlatformTenantSelection = Boolean(selectedPlatformTenant?.key);

  const [step, setStep] = useState(1);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [passportImageFile, setPassportImageFile] = useState(null);
  const [tenantLicensePayload, setTenantLicensePayload] = useState(null);
  const [licenseLoading, setLicenseLoading] = useState(false);

  const [formData, setFormData] = useState({
    employee_code: '',
    name: '',
    email: '',
    iqama_no: '',
    passport_no: '',
    passport_issue_date: '',
    passport_expiry_date: '',
    position: '',
    department: '',
    cost_center: '',
    site: '',
    project_id: '',
    nationality: '',
    birth_date: '',
    joining_date: '',
    contract_type: '',
    contract_issue_date: '',
    contract_expiry_date: '',
    bank_name: '',
    iban: '',
    account_number: '',
    password: '',
    role: 'employee',
    actual_role: '',
    gender: 'male',
    manager_id: '',
  });

  const [managers, setManagers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [roleOptions, setRoleOptions] = useState(FALLBACK_ROLE_OPTIONS);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(''); // '', 'passport', 'cv'

  const handlePassportScan = async (file) => {
    if (!file) return;
    setPassportImageFile(file);
    setScanning('passport');
    setError('');
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await api.post('/hr/document-ai/passport', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data?.prefill) setFormData((prev) => ({ ...prev, ...data.prefill }));
      if (data && data.mrz_valid === false) {
        setError('Passport read, but its MRZ checksum did not validate — please verify the fields.');
      }
    } catch (err) {
      setError(mapLaravelError(err).message || 'Passport scan failed.');
    } finally {
      setScanning('');
    }
  };

  const handleCvScan = async (file) => {
    if (!file) return;
    setScanning('cv');
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/hr/document-ai/cv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data?.prefill) setFormData((prev) => ({ ...prev, ...data.prefill }));
    } catch (err) {
      setError(mapLaravelError(err).message || 'CV scan failed.');
    } finally {
      setScanning('');
    }
  };

  useEffect(() => {
    api.get('/admin/users?role=manager').then((res) => setManagers(res.data || [])).catch((err) => console.error('Error loading managers:', err));
    api.get('/admin/departments').then((res) => setDepartments(res.data || [])).catch((err) => console.error('Error loading departments:', err));
    api.get('/projects').then((res) => setProjects(res.data || [])).catch((err) => console.error('Error loading projects:', err));
    fetchRoleOptions()
      .then((payload) => {
        const roles = Array.isArray(payload?.roles) ? payload.roles : [];
        if (roles.length) setRoleOptions(roles);
      })
      .catch((err) => console.error('Error loading role options:', err));
  }, []);

  useEffect(() => {
    if (!isPlatformAdmin || !hasPlatformTenantSelection || !selectedPlatformTenant?.id) {
      setTenantLicensePayload(null);
      setLicenseLoading(false);
      return undefined;
    }
    let active = true;
    setLicenseLoading(true);
    getPlatformTenant(selectedPlatformTenant.id)
      .then((payload) => { if (active) setTenantLicensePayload(payload || null); })
      .catch(() => { if (active) setTenantLicensePayload(null); })
      .finally(() => { if (active) setLicenseLoading(false); });
    return () => { active = false; };
  }, [hasPlatformTenantSelection, isPlatformAdmin, selectedPlatformTenant?.id]);

  const currentLicense = tenantLicensePayload?.current_license || null;
  const licenseUsage = tenantLicensePayload?.license_usage || null;
  const licenseStatus = tenantLicensePayload?.license_status || '';
  const nextLicense = tenantLicensePayload?.next_license || null;

  const licenseAssessment = useMemo(
    () => evaluateLicenseProvisioning(tenantLicensePayload, { targetRole: formData.role, targetStatus: 'active' }),
    [formData.role, tenantLicensePayload]
  );

  const saveBlockedReason = useMemo(() => {
    if (!isPlatformAdmin) return '';
    if (!hasPlatformTenantSelection) return 'Select a tenant from the super admin dashboard before creating users.';
    if (licenseLoading) return 'Current tenant license is still loading.';
    return licenseAssessment.messages[0] || '';
  }, [hasPlatformTenantSelection, isPlatformAdmin, licenseAssessment.messages, licenseLoading]);

  const saveBlocked = Boolean(isPlatformAdmin && saveBlockedReason);

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleProjectChange = (e) => {
    const pid = e.target.value;
    const proj = projects.find((p) => String(p.id) === String(pid));
    setFormData((prev) => ({ ...prev, project_id: pid, site: proj?.name || '' }));
  };

  const tone = roleTone(formData.role);

  const keyFields = ['name', 'employee_code', 'email', 'gender', 'nationality', 'role', 'department', 'joining_date'];
  const doneCount = keyFields.filter((k) => String(formData[k] || '').trim()).length;
  const pct = Math.round((doneCount / keyFields.length) * 100);

  const stepDone = (id) => {
    if (id === 1) return Boolean(formData.name && formData.employee_code && formData.email && formData.nationality);
    if (id === 2) return Boolean(formData.role && formData.department);
    if (id === 3) return Boolean(formData.joining_date);
    return false;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (saveBlockedReason) {
      setError(saveBlockedReason);
      return;
    }
    if (!formData.name.trim() || !formData.employee_code.trim() || !formData.email.trim()) {
      setStep(1);
      setError('Name, employee code and email are required.');
      return;
    }

    const fd = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return;
        fd.append(key, trimmed);
        return;
      }
      fd.append(key, String(value));
    });
    if (profileImageFile) fd.append('profile_image', profileImageFile);
    if (passportImageFile) fd.append('passport_image', passportImageFile);

    setSaving(true);
    api
      .post('/users', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((res) => {
        // The user is created with all gender-appropriate balances already seeded
        // (Annual at 0). Land on the balance list filtered to this employee so the
        // admin can review/adjust every type, instead of adding a duplicate.
        const code = res?.data?.employee_code;
        navigate(code ? `/admin/vacation-balances?user_id=${encodeURIComponent(code)}` : '/admin/users');
      })
      .catch((err) => {
        console.error('Error creating user:', err);
        setError(mapLaravelError(err).message);
        // Surface backend validation by sending the admin back to the identity step.
        setStep(1);
      })
      .finally(() => setSaving(false));
  };

  const fieldSx = { mb: 1.6 };

  const renderStep = () => {
    if (step === 1) {
      return (
        <Stack>
          <TextField label="Full name" name="name" value={formData.name} onChange={handleChange} required fullWidth sx={fieldSx} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.6 }}>
            <TextField label="Employee code" name="employee_code" value={formData.employee_code} onChange={handleChange} required fullWidth />
            <TextField label="Gender" name="gender" select value={formData.gender} onChange={handleChange} required fullWidth>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
            </TextField>
          </Box>
          <TextField label="Work email" name="email" type="email" value={formData.email} onChange={handleChange} required fullWidth sx={{ ...fieldSx, mt: 1.6 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.6 }}>
            <TextField label="Nationality" name="nationality" value={formData.nationality} onChange={handleChange} fullWidth />
            <TextField label="Date of birth" name="birth_date" type="date" value={formData.birth_date} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
          </Box>
        </Stack>
      );
    }
    if (step === 2) {
      return (
        <Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.6, mb: 1.6 }}>
            <TextField label="Role" name="role" select value={formData.role} onChange={handleChange} required fullWidth>
              {roleOptions.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
            </TextField>
            <TextField label="Actual role" name="actual_role" select value={formData.actual_role} onChange={handleChange} fullWidth helperText="Workflow role (defaults to Role)">
              <MenuItem value="">Same as Role</MenuItem>
              {roleOptions.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
            </TextField>
          </Box>
          <TextField label="Position" name="position" value={formData.position} onChange={handleChange} fullWidth sx={fieldSx} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.6, mb: 1.6 }}>
            <TextField label="Department" name="department" select value={formData.department} onChange={handleChange} fullWidth>
              <MenuItem value="">Select department</MenuItem>
              {departments.map((d) => (<MenuItem key={d.id} value={d.name}>{d.name}</MenuItem>))}
            </TextField>
            <TextField label="Manager" name="manager_id" select value={formData.manager_id} onChange={handleChange} fullWidth>
              <MenuItem value="">No manager</MenuItem>
              {managers.map((m) => (<MenuItem key={m.employee_code} value={m.employee_code}>{m.name} ({m.employee_code})</MenuItem>))}
            </TextField>
          </Box>
          <TextField label="Project / site" name="project_id" select value={formData.project_id} onChange={handleProjectChange} fullWidth helperText="Assigns the project (and site name)">
            <MenuItem value="">Select project</MenuItem>
            {projects.map((p) => (<MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>))}
          </TextField>
        </Stack>
      );
    }
    if (step === 3) {
      return (
        <Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.6, mb: 1.6 }}>
            <TextField label="Contract type" name="contract_type" select value={formData.contract_type} onChange={handleChange} fullWidth>
              <MenuItem value="">Select contract type</MenuItem>
              {CONTRACT_TYPES.map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
            </TextField>
            <TextField label="Joining date" name="joining_date" type="date" value={formData.joining_date} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} helperText="Drives service period & accrual" />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.6, mb: 1.6 }}>
            <TextField label="Contract start" name="contract_issue_date" type="date" value={formData.contract_issue_date} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="Contract end" name="contract_expiry_date" type="date" value={formData.contract_expiry_date} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>Bank details</Typography>
          <TextField label="Bank name" name="bank_name" value={formData.bank_name} onChange={handleChange} fullWidth sx={fieldSx} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.6, mb: 1.6 }}>
            <TextField label="IBAN" name="iban" value={formData.iban} onChange={handleChange} fullWidth inputProps={{ maxLength: 34 }} />
            <TextField label="Account number" name="account_number" value={formData.account_number} onChange={handleChange} fullWidth />
          </Box>
          <TextField label="Cost center" name="cost_center" value={formData.cost_center} onChange={handleChange} fullWidth />
        </Stack>
      );
    }
    return (
      <Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.6, mb: 1.6 }}>
          <TextField label="Passport no" name="passport_no" value={formData.passport_no} onChange={handleChange} fullWidth />
          <TextField label="Iqama no" name="iqama_no" value={formData.iqama_no} onChange={handleChange} fullWidth />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.6, mb: 1.6 }}>
          <TextField label="Passport issue" name="passport_issue_date" type="date" value={formData.passport_issue_date} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
          <TextField label="Passport expiry" name="passport_expiry_date" type="date" value={formData.passport_expiry_date} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
        </Box>
        <TextField label="Password" name="password" type="password" value={formData.password} onChange={handleChange} fullWidth sx={fieldSx} helperText="Leave blank if not setting one now" />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.2 }}>
          <Button variant="outlined" component="label" startIcon={<PhotoIcon />}>
            {profileImageFile ? 'Profile photo ✓' : 'Profile photo'}
            <input hidden type="file" accept="image/*" onChange={(e) => setProfileImageFile(e.target.files?.[0] || null)} />
          </Button>
          <Button variant="outlined" component="label" startIcon={<DocsIcon />}>
            {passportImageFile ? 'Passport image ✓' : 'Passport image'}
            <input hidden type="file" accept="image/*" onChange={(e) => setPassportImageFile(e.target.files?.[0] || null)} />
          </Button>
        </Box>
      </Stack>
    );
  };

  return (
    <PageLayout backgroundAccent compact>
      <Box sx={{ maxWidth: 1080, mx: 'auto' }}>
        {/* Command bar */}
        <Box
          component={motion.div}
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduced ? 0.01 : 0.4 }}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '16px',
            p: { xs: 1.6, sm: 2 },
            color: '#fff',
            background: `linear-gradient(120deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${alpha(theme.palette.secondary.main, 0.95)} 130%)`,
          }}
        >
          <Box aria-hidden sx={{ position: 'absolute', right: -30, top: -40, width: 160, height: 160, borderRadius: '50%', background: alpha('#fff', 0.07) }} />
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ position: 'relative' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>Add employee</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>{STEPS[step - 1].hint}</Typography>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.85, fontVariantNumeric: 'tabular-nums' }}>Step {step} / 4</Typography>
          </Stack>

          {/* Step track */}
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1.4, position: 'relative' }}>
            {STEPS.map((s, i) => {
              const active = s.id === step;
              const done = stepDone(s.id) && !active;
              return (
                <React.Fragment key={s.id}>
                  <Stack alignItems="center" spacing={0.4} sx={{ cursor: 'pointer' }} onClick={() => setStep(s.id)}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: '11px',
                        display: 'grid',
                        placeItems: 'center',
                        transition: 'all .25s',
                        backgroundColor: done ? theme.palette.success.main : active ? '#fff' : alpha('#fff', 0.16),
                        color: done ? '#fff' : active ? theme.palette.primary.dark : '#fff',
                        transform: active ? 'scale(1.08)' : 'none',
                      }}
                    >
                      {done ? <CheckIcon fontSize="small" /> : s.icon}
                    </Box>
                    <Typography variant="caption" sx={{ fontSize: 11, opacity: active ? 1 : 0.65 }}>{s.label}</Typography>
                  </Stack>
                  {i < STEPS.length - 1 ? <Box sx={{ flex: 1, height: 2, borderRadius: 2, backgroundColor: stepDone(s.id) ? theme.palette.success.main : alpha('#fff', 0.18) }} /> : null}
                </React.Fragment>
              );
            })}
          </Stack>
        </Box>

        {error ? <Alert severity="error" sx={{ mt: 1.5, borderRadius: '12px' }} onClose={() => setError('')}>{error}</Alert> : null}

        {isPlatformAdmin && !hasPlatformTenantSelection ? (
          <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '12px' }}>Select a tenant from the super admin dashboard before creating users.</Alert>
        ) : isPlatformAdmin ? (
          <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5, borderRadius: '12px' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Tenant license · {selectedPlatformTenant?.name || selectedPlatformTenant?.key}</Typography>
              <Chip size="small" color={licenseStatusColor(licenseStatus)} label={licenseStatusLabel(licenseStatus)} />
            </Stack>
            {licenseLoading ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}><CircularProgress size={16} /><Typography variant="caption" color="text.secondary">Loading current license…</Typography></Stack>
            ) : currentLicense ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{formatLicenseWindow(currentLicense)} · {formatLicenseSummary(licenseUsage)}{nextLicense ? ` · next starts ${nextLicense.valid_from}` : ''}</Typography>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>No active tenant license. User creation is blocked until a current license window exists.</Alert>
            )}
            {saveBlockedReason && !licenseLoading ? <Alert severity="error" sx={{ mt: 1 }}>{saveBlockedReason}</Alert> : null}
          </Paper>
        ) : null}

        {/* Body: wizard + live card */}
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.5fr) minmax(280px, 1fr)' }, gap: 1.5, mt: 1.5, alignItems: 'start' }}>
          <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: '14px' }}>
            {/* Scan-to-autofill */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                p: 1,
                mb: 2,
                borderRadius: '12px',
                border: '1.5px dashed',
                borderColor: scanning ? theme.palette.primary.main : theme.palette.divider,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              }}
            >
              {scanning ? <CircularProgress size={20} /> : <SparkleIcon sx={{ color: 'primary.main' }} />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {scanning === 'passport' ? 'Reading passport…' : scanning === 'cv' ? 'Reading CV…' : 'Scan passport or CV to auto-fill'}
                </Typography>
                <Typography variant="caption" color="text.secondary">The fields fill themselves — you just review.</Typography>
              </Box>
              <Button size="small" component="label" disabled={scanning !== ''}>
                Passport
                <input hidden type="file" accept="image/*,application/pdf" onChange={(e) => handlePassportScan(e.target.files?.[0] || null)} />
              </Button>
              <Button size="small" component="label" disabled={scanning !== ''}>
                CV
                <input hidden type="file" accept=".pdf,.doc,.docx,.txt,image/*" onChange={(e) => handleCvScan(e.target.files?.[0] || null)} />
              </Button>
            </Box>

            {/* Step header */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Box sx={{ width: 30, height: 30, borderRadius: '9px', display: 'grid', placeItems: 'center', color: 'primary.main', backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>{STEPS[step - 1].icon}</Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.15 }}>{STEPS[step - 1].label}</Typography>
                <Typography variant="caption" color="text.secondary">{STEPS[step - 1].hint}</Typography>
              </Box>
            </Stack>

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={reduced ? { opacity: 0 } : { opacity: 0, x: 14 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduced ? { opacity: 0 } : { opacity: 0, x: -14 }}
                transition={{ duration: reduced ? 0.01 : 0.28, ease: [0.16, 1, 0.3, 1] }}
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>

            {/* Nav */}
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button type="button" variant="outlined" startIcon={<BackIcon />} disabled={step === 1 || saving} onClick={() => setStep((s) => Math.max(1, s - 1))}>Back</Button>
              <Button type="button" variant="text" color="inherit" sx={{ ml: 'auto' }} onClick={() => navigate('/admin/users')} disabled={saving}>Cancel</Button>
              {step < 4 ? (
                <Button type="button" variant="contained" endIcon={<ForwardIcon />} onClick={() => setStep((s) => Math.min(4, s + 1))}>Next</Button>
              ) : (
                <Button type="submit" variant="contained" endIcon={<SaveIcon />} disabled={saving || saveBlocked}>
                  {saving ? 'Saving…' : 'Save & set balance'}
                </Button>
              )}
            </Stack>
          </Paper>

          {/* Live ID card + completion */}
          <Stack spacing={1.5} sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
            <IdCard form={formData} tone={tone} roleLabel={(roleOptions.find((o) => String(o.value) === String(formData.role)) || {}).label} />
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CompletionRing pct={pct} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{pct === 100 ? 'Ready to save' : 'Profile completeness'}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>{doneCount} of {keyFields.length} key fields</Typography>
              </Box>
            </Paper>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'info.main', px: 0.5 }}>
              <ForwardIcon fontSize="small" />
              <Typography variant="caption">After saving, you'll set up this hire's vacation balance.</Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </PageLayout>
  );
}
